
import { useNavigate } from 'react-router-dom';
import { Sidebar, SidebarHeader, SidebarContent, SidebarTrigger } from '@/components/ui/sidebar';
import { WorkspaceSelector } from '../workspace/WorkspaceSelector';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { NavigationMenu } from './sidebar/NavigationMenu';
import { OrganizationList } from './sidebar/OrganizationList';
import { RecentBoards } from './sidebar/RecentBoards';
import { useOrganizations } from './sidebar/useOrganizations';
import { WorkspaceSidebarProps } from './sidebar/types';

export const WorkspaceSidebar = ({ onWorkspaceChange }: WorkspaceSidebarProps) => {
  const navigate = useNavigate();
  const { data: organizations = [], refetch: refetchOrganizations } = useOrganizations();

  const { data: recentBoardsData } = useQuery({
    queryKey: ['recent-boards'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('boards')
        .select('id, name, last_viewed_at')
        .not('last_viewed_at', 'is', null)
        .order('last_viewed_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    }
  });

  const handleWorkspaceClick = (workspaceId: string) => {
    onWorkspaceChange?.(workspaceId);
    navigate('/dashboard');
  };

  const handleBoardClick = async (boardId: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const { error } = await supabase
      .from('boards')
      .update({ last_viewed_at: new Date().toISOString() })
      .eq('id', boardId);

    if (!error) {
      navigate(`/board/${boardId}`);
    }
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center justify-between">
          <WorkspaceSelector onWorkspaceSelect={onWorkspaceChange} />
          <SidebarTrigger className="bg-sidebar-accent hover:bg-sidebar-accent/80" />
        </div>
      </SidebarHeader>
      <SidebarContent>
        <OrganizationList
          organizations={organizations}
          onWorkspaceClick={handleWorkspaceClick}
          onBoardClick={handleBoardClick}
          onOrganizationRename={refetchOrganizations}
        />
        <NavigationMenu onWorkspaceChange={onWorkspaceChange} />
        <RecentBoards
          boards={recentBoardsData}
          onBoardClick={handleBoardClick}
        />
      </SidebarContent>
    </Sidebar>
  );
};
